import React, { useMemo } from 'react';
import trans from 'translate';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { telegramLink } from 'constants/general';
import { createHPoolHandleTokenPercentageChange, getBaseToken } from 'services/createHPoolService';
import SliderTokensList from 'components/Common/SliderTokensList/SliderTokensList';
import TokensListsSearch from 'components/Common/TokensListsSearch/TokensListsSearch';
import YellowAlert from 'components/Common/YellowAlert/YellowAlert';
import Loader from 'components/Common/Loader/Loader';
import {
  setCreateHPoolChooseTokensSearchAction,
  createHPoolAddTokenFromSearchAction,
  createHPoolHandleTokenPercentageChangeAction,
  createHPoolHandleTokenLockUnlockAction,
  createHPoolHandleTokenDeleteAction,
  setChooseTicketsAlertAction,
} from '../../../../redux/actions/createHPoolActions';

import CreateHPoolControls from '../CreateHPoolControls/CreateHPoolControls';
import { EDIT_TOKEN_VIEW_STAGE } from '../../../../constants/hpool';
import configJson from '../../../../config/config.json';
import withLoader from '../WithLoader';

import './CreateHPoolChooseTokens.scss';
import { getValueByMultiProps, getValueByMultiPropsLowerCased } from '../../../../utils/mapper';
import { openSupportedTokensModal } from '../../../../redux/actions/modalActions';
import { createNotificationAction } from '../../../../redux/actions/uiActions';
import SupportedTokensLink from '../../../Modals/HordSupportedTokens/SupportedTokensLink/SupportedTokensLink';
import ProjectsTable from '../CreateHPoolReview/ProjectsTable/ProjectsTable';

export const CreateHPoolChooseTokensInner = ({
  setCreateHPoolChooseTokensSearchAction,
  tokensListSearchVal,
  searchingTokens,
  searchingTokensError,
  tokensSearchResults,
  createHPoolAddTokenFromSearchAction,
  addedTokens,
  createHPoolHandleTokenPercentageChangeAction,
  createHPoolHandleTokenLockUnlockAction,
  createHPoolHandleTokenDeleteAction,
  chooseTokensAlert,
  setChooseTicketsAlertAction,
  stage,
  requiredTokensList,
  actionCol,
  mostUsedTokens,
  createHPool,
  openSupportedTokensModal,
  isVpool,
}) => {
  const dispatch = useDispatch();
  const { configTokens = [] } = window;
  const baseTokenName = configTokens?.[0]?.symbol?.toLocaleLowerCase?.();
  const lockedTokens = [baseTokenName, `w${baseTokenName}`];
  const symbolKeys = ['symbol', 'token_symbol'];

  const getTokenSymbol = (token) => getValueByMultiProps(token, symbolKeys);

  // Some tokens will always be locked like the ETH
  const lockedTokensCheck = (tokens) =>
    tokens.map((el) => (lockedTokens.includes(getTokenSymbol(el).toLowerCase()) ? { ...el, locked: true } : el));

  const [requiredTokens, otherTokens] = useMemo(() => {
    const _requiredTokens = lockedTokensCheck(
      addedTokens?.filter((el) => requiredTokensList?.includes(getTokenSymbol(el).toLowerCase())),
    )?.sort((firstEl, secondEl) => firstEl?.token_symbol?.localeCompare(secondEl?.token_symbol));

    const _otherTokens = addedTokens.filter((el) => !requiredTokensList.includes(getTokenSymbol(el).toLowerCase()));

    return [_requiredTokens, _otherTokens];
  }, [addedTokens, requiredTokensList]);

  const ethLeftover = useMemo(() => {
    if (!addedTokens.length) return;
    const baseToken = getBaseToken(addedTokens, baseTokenName);
    const left = baseToken ? baseToken.percentage - (baseToken.min || 4) : 0;
    return left;
  }, [addedTokens, baseTokenName]);

  const detectMaxChangeLimitReached = (token, newVal) => {
    const { percentage: prevVal } = token;
    if (newVal === prevVal && !ethLeftover) {
      const errMsg = trans('hpool.edit_tokens_min_limit_error', { coin: configJson.baseCoin }, null, true);
      throw new Error(errMsg);
    }
  };

  const onChange = (...params) => {
    try {
      const [newVal, changedToken] = params;
      detectMaxChangeLimitReached(changedToken, newVal);
      createHPoolHandleTokenPercentageChangeAction(...params, ethLeftover);
    } catch (error) {
      dispatch(createNotificationAction('error', error.message, 4000));
    }
  };

  return (
    <>
      {addedTokens.length > 0 ? (
        <div className="choose-tokens-wrapper">
          <SliderTokensList
            tokens={requiredTokens}
            handleTokenSliderChange={onChange}
            sliderCheck={createHPoolHandleTokenPercentageChange}
            handleTokenLock={createHPoolHandleTokenLockUnlockAction}
            handleTokenDelete={createHPoolHandleTokenDeleteAction}
            allTokens={addedTokens}
            ethLeftover={ethLeftover}
            actionCol={actionCol}
          />

          <div className="subtitle d-flex flex-between align-center position-relative flex-center-x add-tokens-wrapper">
            <span>{isVpool ? trans('vpool.add_tokens_to_your_vpool') : trans('pools.add_tokens_to_your_hpool')}</span>
            <span className="font-sm light-gray-text check-supported-tokens" onClick={openSupportedTokensModal}>
              <SupportedTokensLink text="pools.check_supported_tokens" hideImg />
            </span>
          </div>

          <TokensListsSearch
            selectedTokens={addedTokens}
            handleChange={setCreateHPoolChooseTokensSearchAction}
            reducerVal={tokensListSearchVal}
            placeholder={trans('pools.search_for_tokens_or_enter')}
            searching={searchingTokens}
            searchingErr={searchingTokensError}
            searchResults={tokensSearchResults}
            handleSearchResultClick={createHPoolAddTokenFromSearchAction}
            defaultTokens={mostUsedTokens?.tokens}
          />

          {chooseTokensAlert !== 'false' && (
            <YellowAlert
              dismissFunc={setChooseTicketsAlertAction}
              text={trans('yellow_alert.choose_tokens', {
                telegramLink: (chunk) => (
                  <a target="_blank" rel="noopener noreferrer" href={telegramLink} className="explorer-link">
                    {chunk}
                  </a>
                ),
              })}
            />
          )}

          <SliderTokensList
            tokens={otherTokens}
            handleTokenSliderChange={onChange}
            sliderCheck={createHPoolHandleTokenPercentageChange}
            handleTokenLock={createHPoolHandleTokenLockUnlockAction}
            handleTokenDelete={createHPoolHandleTokenDeleteAction}
            allTokens={addedTokens}
            ethLeftover={ethLeftover}
            actionCol={actionCol}
            editMode={stage === EDIT_TOKEN_VIEW_STAGE}
          />
          {/* <span className="subtitle d-flex flex-center-x">{trans('pools.add_tokens_to_your_projects_table')}</span>
          <ProjectsTable /> */}
          {/* Going back to choose pool type will be disabled in case it's already been decided */}
          {stage === EDIT_TOKEN_VIEW_STAGE ? null : <CreateHPoolControls noBack={isVpool !== undefined} />}
        </div>
      ) : (
        <Loader message={trans('loading.create_hpool')} />
      )}
    </>
  );
};

CreateHPoolChooseTokensInner.propTypes = {
  setCreateHPoolChooseTokensSearchAction: PropTypes.func.isRequired,
  createHPoolHandleTokenLockUnlockAction: PropTypes.func.isRequired,
  createHPoolAddTokenFromSearchAction: PropTypes.func.isRequired,
  createHPoolHandleTokenPercentageChangeAction: PropTypes.func.isRequired,
  createHPoolHandleTokenDeleteAction: PropTypes.func.isRequired,
  setChooseTicketsAlertAction: PropTypes.func.isRequired,
  openSupportedTokensModal: PropTypes.func.isRequired,

  tokensListSearchVal: PropTypes.string.isRequired,
  actionCol: PropTypes.object,
  searchingTokens: PropTypes.bool.isRequired,
  searchingTokensError: PropTypes.string.isRequired,
  tokensSearchResults: PropTypes.array.isRequired,

  addedTokens: PropTypes.array,
  chooseTokensAlert: PropTypes.string.isRequired,
  requiredTokensList: PropTypes.array,
  stage: PropTypes.number,
  mostUsedTokens: PropTypes.object.isRequired,
  createHPool: PropTypes.object,
  isVpool: PropTypes.bool,
};

const mapStateToProps = ({ createHPool, general }) => ({
  tokensListSearchVal: createHPool.tokensListSearchVal,
  searchingTokens: createHPool.searchingTokens,
  searchingTokensError: createHPool.searchingTokensError,
  tokensSearchResults: createHPool.tokensSearchResults,
  addedTokens: createHPool.addedTokens,
  chooseTokensAlert: createHPool.chooseTokensAlert,
  requiredTokensList: general.requiredTokens,
  mostUsedTokens: createHPool.mostUsedTokens,
  createHPool,
});

const mapDispatchToProps = {
  setCreateHPoolChooseTokensSearchAction,
  createHPoolAddTokenFromSearchAction,
  createHPoolHandleTokenPercentageChangeAction,
  createHPoolHandleTokenLockUnlockAction,
  createHPoolHandleTokenDeleteAction,
  setChooseTicketsAlertAction,
  openSupportedTokensModal,
};

export default withLoader(connect(mapStateToProps, mapDispatchToProps)(CreateHPoolChooseTokensInner));

CreateHPoolChooseTokensInner.defaultProps = {
  stage: null,
  addedTokens: [],
  requiredTokensList: [],
  actionCol: {
    title: 'common.lock',
    render: () => null,
  },
  createHPool: {},
  isVpool: null,
};
